/**
 * Modelo da vitrine do Catálogo de Experiências: o que cada cartão mostra, calculado a partir do
 * documento que o motor (`E1djhtF`) monta. As regras são as do card antigo
 * (`ExperienceSelectCard`), reunidas numa função pura para o visual novo não precisar conhecê-las.
 */

import { Combo, ExperienceCatalogResponse, PremiacaoIsolada } from 'types/experienceCatalog';
import { getNamedDates } from 'services/utils';
import { getExperienceTypeTitle, stripCatalogPrefix } from 'utils/experience-catalog';

export type TipoDaExperiencia = 'unico' | 'combo';

/** Ciclos oferecidos pelo catálogo: o 2S2026 fica de fora pelo teto das páginas de fechamento. */
export const PRIMEIRO_CICLO_DO_CATALOGO = getNamedDates().ciclo_1S2025;
export const ULTIMO_CICLO_DO_CATALOGO = getNamedDates().ciclo_1S2026;

/** Páginas de regras em HTML (`rules1`, `rules2`), na ordem e sem as vazias. */
export function paginasDeRegras(regras?: { rules1?: string; rules2?: string }): string[] {
  return [regras?.rules1, regras?.rules2].filter((pagina): pagina is string => !!pagina);
}

/**
 * Nome da experiência, igual na vitrine, no modal e na página da experiência escolhida (D-14):
 * a premiação isolada pelo tipo e valor ("109.400 Pontos Livelo"); o combo, ou a isolada sem
 * valor (resgate de ciclo anterior), pelo título sem o prefixo de catalogação.
 */
export function nomeDaExperiencia(tipoDoItem: string | undefined, titulo: string | undefined, valor?: number | string): string {
  return tipoDoItem && tipoDoItem !== 'combo' && valor ? getExperienceTypeTitle(tipoDoItem, valor) : stripCatalogPrefix(titulo);
}
export type FiltroDoCatalogo = 'todas' | TipoDaExperiencia;

export interface CartaoDeExperiencia {
  /** `itemIdPurchase`: identifica o cartão e é o item da compra. */
  id: string;
  tipo: TipoDaExperiencia;
  /** Título da frente: o nome do tipo na premiação isolada, o título sem prefixo no combo. */
  titulo: string;
  /** Título do item sem o prefixo de catalogação ("(Combo 28) - "): verso e "Sua escolha:". */
  tituloDoItem: string;
  /** Linha da estrela (`regrasDeUso.subtitle`). */
  tagline?: string;
  descricao?: string;
  imagem?: string;
  /** Aviso do motor quando o card promete algo que o jogador não tem. */
  aviso?: string;
  /** Vagas restantes, só quando o cartão deve mostrá-las. */
  vagasRestantes?: number;
  esgotada: boolean;
  promocoesLivelo: boolean;
  proximaData?: string;
  /** Páginas de regras em HTML (`rules1`, `rules2`), na ordem. */
  regras: string[];
  /** Nome da escolha nos painéis do modal e no "Escolha registrada!". */
  nomeDaEscolha: string;
  /** Linha "Sua escolha:" do modal de confirmação. */
  escolha: { quantidade?: string; destaque: string; complemento?: string };
  item: PremiacaoIsolada | Combo;
}

function estoque(item: PremiacaoIsolada | Combo) {
  const resgatados = Math.max(Number(item.reedem) || 0, 0);
  const limitado = item.amount !== -1;
  const restante = limitado ? Math.max(item.amount - resgatados, 0) : null;
  return { restante, esgotada: limitado && restante === 0 };
}

/** Quantidade antes do título, na premiação isolada (o combo não tem). */
function quantidadeDaIsolada(item: PremiacaoIsolada): string {
  if (item.type !== 'pontos_Livelo' && 'total' in item) return String(item.total);
  if ('valor' in item) return (item.valor ?? '').toLocaleString('pt-BR');
  return '1';
}

function cartaoBase(item: PremiacaoIsolada | Combo) {
  const { restante, esgotada } = estoque(item);
  return {
    id: item.itemIdPurchase,
    tituloDoItem: stripCatalogPrefix(item.title),
    tagline: item.regrasDeUso?.subtitle || undefined,
    descricao: item.description || undefined,
    imagem: item.image || undefined,
    vagasRestantes: item.amount > 1 && restante !== null && restante > 0 ? restante : undefined,
    esgotada,
    proximaData: item.nextDate || undefined,
    regras: paginasDeRegras(item.regrasDeUso),
    item,
  };
}

function cartaoIsolado(item: PremiacaoIsolada): CartaoDeExperiencia {
  const base = cartaoBase(item);
  const titulo = nomeDaExperiencia(item.type, item.title, item.valor);
  return {
    ...base,
    tipo: 'unico',
    titulo,
    promocoesLivelo: item.type === 'pontos_Livelo',
    nomeDaEscolha: titulo,
    escolha: { quantidade: quantidadeDaIsolada(item), destaque: base.tituloDoItem },
  };
}

function cartaoCombo(item: Combo): CartaoDeExperiencia {
  const base = cartaoBase(item);
  // No combo, a frase do motor (com o complemento) é a escolha; o título é a mesma frase sem valor.
  const frase = item.description || base.tituloDoItem;
  return {
    ...base,
    tipo: 'combo',
    titulo: base.tituloDoItem,
    aviso: item.aviso || undefined,
    promocoesLivelo: false,
    nomeDaEscolha: frase,
    escolha: { destaque: frase, complemento: item.regrasDeUso?.subtitle },
  };
}

/** Cartão de um item gravado (o `extra` do resgate): o combo se declara pelo `type`. */
export function montarCartao(item: PremiacaoIsolada | Combo): CartaoDeExperiencia {
  return (item as PremiacaoIsolada).type === 'combo' ? cartaoCombo(item as Combo) : cartaoIsolado(item as PremiacaoIsolada);
}

/** Cartões da vitrine: premiações isoladas primeiro (Pontos Livelo à frente), depois os combos. */
export function montarCartoes(catalogo?: ExperienceCatalogResponse): CartaoDeExperiencia[] {
  const isoladas = (catalogo?.premiacaoIsolada ?? [])
    .slice()
    .sort((a, b) => Number(b.type === 'pontos_Livelo') - Number(a.type === 'pontos_Livelo'));
  return [...isoladas.map(cartaoIsolado), ...(catalogo?.combos ?? []).map(cartaoCombo)];
}

export function filtrarCartoes(cartoes: CartaoDeExperiencia[], filtro: FiltroDoCatalogo): CartaoDeExperiencia[] {
  return filtro === 'todas' ? cartoes : cartoes.filter(cartao => cartao.tipo === filtro);
}

/** Resgate gravado do ciclo (`find/resgate_experiencias`): o `extra` é a cópia do item no momento da escolha. */
export interface ResgateDoCiclo {
  extra?: PremiacaoIsolada | Combo;
}

/** O `itemIdPurchase` da experiência resgatada, que é o `id` do cartão dela na vitrine. */
export function idDaEscolha(resgate?: ResgateDoCiclo | null): string | undefined {
  return resgate?.extra?.itemIdPurchase || undefined;
}

/**
 * A escolha do ciclo e os cartões que o jogador ainda poderia escolher (VTR-03, VTR-04, VTR-06).
 * Sem o cartão na vitrine (item inativado, faixa da consulta), a escolha é montada da cópia gravada.
 */
export function separarEscolha(
  cartoes: CartaoDeExperiencia[],
  resgate?: ResgateDoCiclo | null,
): { escolhido?: CartaoDeExperiencia; outros: CartaoDeExperiencia[] } {
  const id = idDaEscolha(resgate);
  if (!id || !resgate?.extra) return { escolhido: undefined, outros: cartoes };
  return {
    escolhido: cartoes.find(cartao => cartao.id === id) ?? montarCartao(resgate.extra),
    outros: cartoes.filter(cartao => cartao.id !== id),
  };
}

/**
 * O item a recomprar quando a troca falha (TRC-13): o do cartão da escolha atual, montado agora
 * pelo motor; sem o cartão na vitrine (item inativado), a cópia gravada no resgate.
 */
export function itemDaEscolhaAtual(cartoes: CartaoDeExperiencia[], resgate?: ResgateDoCiclo | null): PremiacaoIsolada | Combo | undefined {
  const id = idDaEscolha(resgate);
  if (!id) return undefined;
  return cartoes.find(cartao => cartao.id === id)?.item ?? resgate?.extra;
}

const doisDigitos = (n: number) => String(n).padStart(2, '0');

/** "19 dias, 11:59:59", "1 dia, 03:00:00" ou "03:00:00"; zerado quando o prazo passou. */
export function formatarContagem(milissegundos: number): string {
  const segundos = Math.max(0, Math.floor(milissegundos / 1000));
  const dias = Math.floor(segundos / 86400);
  const horas = Math.floor((segundos % 86400) / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const prefixo = dias > 1 ? `${dias} dias, ` : dias === 1 ? '1 dia, ' : '';
  return `${prefixo}${doisDigitos(horas)}:${doisDigitos(minutos)}:${doisDigitos(segundos % 60)}`;
}

/** Fração decorrida da janela, de 0 a 100. */
export function progressoDaJanela(agora: number, inicio: Date, fim: Date): number {
  const total = fim.getTime() - inicio.getTime();
  if (total <= 0) return 100;
  return Math.min(100, Math.max(0, ((agora - inicio.getTime()) / total) * 100));
}

export function formatarData(data: Date): string {
  return data.toLocaleDateString('pt-BR');
}

export function formatarDataHora(data: Date): string {
  return data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
